import { Clear, Search } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Skeleton,
  Card,
} from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { addToCartAction } from "../../../../../redux/cart/cart.reducers";
import { CartReduxService } from "../../../../../redux/cart/CartReduxService";
import * as CategoryActions from "../../../../../redux/categories/categories.actions";
import * as CategoryReducers from "../../../../../redux/categories/categories.reducers";
import * as ProductActions from "../../../../../redux/products/products.actions";
import * as ProductReducers from "../../../../../redux/products/products.reducers";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../../redux/store";
import { textFieldStyle } from "../../../../../styles/TextFieldStyle";
import { primary, secondary } from "../../../../../themes/theme";
import {
  ICategoryResponseView,
  ISubCategoryView,
} from "../../../../categories/models/ICategoryResponseView";
import LayoutHeading from "../../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../../ui/components/spinner/SpinnerUI";
import ProductSidebar from "../../../components/ProductSidebar";
import { IProductResponseView } from "../../../models/IProductResponseView";
import ProductCard2 from "../../../components/product-card/ProductCard2";
import NoProductsSvg from "../../../../../assets/svg/NoProductsSvg";

const selectedFilterProducts = createSelector(
  (state: RootState) => state.productFeature.products,
  (state: RootState) => state.productFeature.searchQuery,
  (products, searchQuery) => {
    if (!searchQuery.trim()) {
      return products;
    }
    const sanitizedQuery = searchQuery.trim().toLowerCase();
    return products.filter(
      (product) =>
        product._id?.toLowerCase().includes(sanitizedQuery) ||
        product.title.toLowerCase().includes(sanitizedQuery) ||
        product.brand.toLowerCase().includes(sanitizedQuery) ||
        product.categoryObj.name.toLowerCase().includes(sanitizedQuery) ||
        product.subCategoryObj.name.toLowerCase().includes(sanitizedQuery)
    );
  }
);

interface CataloguePageProps {
  heading: string;
  description: string;
  categoryFilter: string;
}

const CataloguePage: React.FC<CataloguePageProps> = ({
  heading,
  description,
  categoryFilter,
}) => {
  const dispatch: AppDispatch = useAppDispatch();
  const [subCategories, setSubCategories] = useState<ISubCategoryView[]>([]);
  const [category, setCategory] = useState<ICategoryResponseView>(
    {} as ICategoryResponseView
  );
  const [filteredProducts, setFilteredProducts] = useState<
    IProductResponseView[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const searchFilteredProducts: IProductResponseView[] = useSelector(
    selectedFilterProducts
  );
  const [filterLoading, setFilterLoading] = useState<boolean>(false);

  const productReduxState: ProductReducers.InitialState = useSelector(
    (state: RootState) => {
      return state[ProductReducers.productFeatureKey];
    }
  );

  const categoryReduxState: CategoryReducers.InitialState = useSelector(
    (state: RootState) => {
      return state[CategoryReducers.categoryFeatureKey];
    }
  );

  const { loading, products, searchQuery } = productReduxState;
  const { categories } = categoryReduxState;

  useEffect(() => {
    setIsLoading(true);
    dispatch(CategoryActions.getAllCategoriesAction());
  }, [dispatch]);

  const handleSearch = (query: string) => {
    dispatch(ProductReducers.setSearchQuery(query));
  };

  const handleClear = () => {
    dispatch(ProductReducers.setSearchQuery(""));
    dispatch(ProductReducers.setSearchFilteredProducts(products));
  };

  useEffect(() => {
    if (categories.length > 0) {
      const category: ICategoryResponseView | undefined = categories.find(
        (item) =>
          item.name
            .toLowerCase()
            .trim()
            .includes(categoryFilter.toLowerCase().trim())
      );
      if (category) {
        setCategory(category);
        if (category.subCategories) {
          setSubCategories(
            category.subCategories.map((item) => {
              return {
                _id: item._id,
                name: item.name,
                description: item.description,
                isChecked: true,
              };
            })
          );
        }
      }
    }
  }, [categories, categoryFilter]);

  useEffect(() => {
    if (category && Object.keys(category).length > 0) {
      dispatch(
        ProductActions.getAllProductsWithCategoryIdAction({
          categoryId: category._id,
        })
      );
    }
  }, [category, dispatch]);

  useEffect(() => {
    if (products.length > 0 || !loading) {
      setFilteredProducts(products);
      setIsLoading(false);
    }
  }, [products, loading]);

  const filteredTheProducts = (subCategories: ISubCategoryView[]) => {
    let subs = subCategories
      .map((item) => {
        if (item.isChecked) {
          return item._id;
        }
      })
      .filter((item) => item !== undefined);
    setFilterLoading(true);
    setTimeout(() => {
      setFilteredProducts(
        products.filter((item) => subs.includes(item?.subCategoryObj?._id))
      );
      setFilterLoading(false);
    }, 300);
  };

  const clickAddToCart = (product: IProductResponseView) => {
    dispatch({
      type: `${addToCartAction}`,
      payload: {
        product: CartReduxService.convertToProductEntity(product),
        count: 1,
      },
    });
  };

  const productsToDisplay = searchQuery
    ? searchFilteredProducts
    : filteredProducts;

  return (
    <>
      <Box sx={{ pb: 2, mb: 2 }}>
        <MainNavBar />
        <LayoutHeading description={description} heading={heading} />
        {loading || filterLoading || isLoading ? (
          <>
            <SpinnerUI />
            <Grid
              container
              spacing={2}
              sx={{
                pl: { xs: 1, sm: 1, md: 10, lg: 10 },
                pr: { xs: 1, sm: 1, md: 5, lg: 5 },
                pt: 2,
              }}
            >
              {[...Array(15)].map((_, index) => (
                <Grid item xs={6} sm={4} md={3} lg={3} xl={2.4} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      maxWidth: { xs: "100%", sm: "100%" },
                      width: { xs: "100%", sm: "100%" },
                      aspectRatio: {
                        xs: "1/1.99",
                        sm: "1 / 1.85",
                        md: "1 / 1.85",
                        lg: "1 / 1.85",
                      },
                      maxHeight: {
                        xs: 350,
                        sm: 370,
                        md: 400,
                        lg: 420,
                        xl: 440,
                      },
                      height: "auto",
                      borderRadius: "10px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Skeleton animation="wave" variant="rectangular" height={"60%"} />
                    <Skeleton animation="wave" height={50} width={"50%"} />
                    <Skeleton animation="wave" height={80} />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Skeleton animation="wave" width="30%" height={45} />
                      <Box
                        width={"40%"}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "5px",
                        }}
                      >
                        <Skeleton variant="circular" animation="wave" width="40px" height={40} />
                        <Skeleton variant="circular" animation="wave" width="40px" height={40} />
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <>
            <>
              {/* Search Bar  */}
              <Grid
                container
                spacing={1}
                sx={{
                  position: "sticky",
                  top: { xs: 0, sm: 0, md: 78 },
                  background: secondary[100],
                  backdropFilter: "blur(50px)",
                  zIndex: 1000,
                  pl: { xs: 1, sm: 1, md: 10, lg: 10 },
                  mb: 2,
                }}
              >
                <Grid item xs={10} sm={10} md={11} lg={11} sx={{ py: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={`Search ${category.name} Products`}
                    value={searchQuery}
                    sx={textFieldStyle}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClear}
                            sx={{
                              color: "grey",
                              background: "transparent",
                              "&:hover": {
                                color: primary[900],
                                background: "transparent",
                              },
                            }}
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs={2}
                  sm={2}
                  md={1}
                  lg={1}
                  sx={{
                    textAlign: "center",
                    py: 1,
                  }}
                >
                  <ProductSidebar
                    subCategories={subCategories}
                    setSubCategories={setSubCategories}
                    filteredTheProducts={filteredTheProducts}
                    categoryName={category?.name}
                  />
                </Grid>
              </Grid>
            </>

            {searchQuery && (
              <Grid
                item
                xs={12}
                sx={{
                  zIndex: 1300,
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  height: "100%",
                  pb: 1,
                  pl: { xs: 1, sm: 1, md: 10, lg: 10 },
                  position: "sticky",
                  background: secondary[100],
                  top: { xs: 151, sm: 151, md: 152 },
                }}
              >
                <Typography sx={{ fontFamily: "Ubuntu" }}>
                  {productsToDisplay.length === 0 ? (
                    <>
                      No matching results with{" "}
                      <span style={{ color: "orangered" }}>{searchQuery}</span>
                    </>
                  ) : (
                    <>
                      Showing {productsToDisplay.length} result
                      {productsToDisplay.length > 1 ? "s" : ""} with{" "}
                      <span style={{ color: "orangered" }}>{searchQuery}</span>
                    </>
                  )}
                </Typography>
              </Grid>
            )}

            {productsToDisplay.length === 0 ? (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <NoProductsSvg
                  width="300px"
                  height="250px"
                  text="No Products"
                />
              </Grid>
            ) : (
              <Grid
                container
                spacing={1}
                sx={{
                  pl: { xs: 1, sm: 1, md: 10, lg: 10 },
                  pr: { xs: 1, sm: 1, md: 5, lg: 5 },
                  pt: 2,
                }}
              >
                {productsToDisplay.map((product) => (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    lg={3}
                    xl={2.4}
                    key={product._id}
                  >
                    <ProductCard2
                      product={product}
                      clickAddToCart={clickAddToCart}
                      categoryName={category?.name}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default CataloguePage;
